import React from "react";
import PropTypes from "prop-types";
import { List } from "antd";
import TableWithControls from "../common/table-with-controls";
import { getCases, esqCases, ES_URL } from "../../services/api-ptab";
import { toDateString, toSetString, toCaseDetailLink, toPatentDetailLink, toFilingS3Link, colorPTABStatus } from "../../helpers/converters";
import { toString, upperFirst } from "lodash";
import { PET_TYPE_MAP } from "./cases-toolbar";
import { withTopCompanies } from "../common/top-companies";

const styles = {
  subList: {
    marginLeft: 25
  }
};

const CasesTableContainer = ({
  queries,
  onQueryChange,
  shouldExport = false,
  shouldSelectColumns = true,
  size,
  companiesRenderer
}) => {
  const [queriesCurrent, setQueriesCurrent] = React.useState(queries); // include transient pagination states
  const colIndicesDefault = [
    "case_number",
    "patent.patent_number",
    "filing_date",
    "up_institution_date",
    "up_termination_date",
    "up_status_concat",
    "petitioners",
    "up_pet_type",
    "patent_owners",
    "ptab_tech_center",
    "up_industry",
    "up_po_type"
  ];
  let allCols = [
    {
      dataIndex: "case_number",
      title: "Case",
      width: 150,
      sorter: true,
      render: toCaseDetailLink
    },
    {
      dataIndex: ["patent", "patent_number"],
      title: "Patent",
      width: 120,
      render: (val, record) => toPatentDetailLink(record.patent.patent_number)
    },
    {
      dataIndex: "filing_date",
      title: "Filing Date",
      width: 120,
      sorter: true,
      render: toDateString
    },
    {
      dataIndex: "up_institution_date",
      title: "Inst. Date",
      width: 120,
      sorter: true,
      render: toDateString
    },
    {
      dataIndex: "up_termination_date",
      title: "Term. Date",
      width: 120,
      sorter: true,
      render: toDateString
    },
    {
      dataIndex: "up_status_concat",
      title: "Status",
      width: 120,
      sorter: true,
      render: colorPTABStatus
    },
    {
      dataIndex: "petitioners",
      title: "Petitioner",
      width: 200,
      render: companiesRenderer
    },
    {
      dataIndex: "up_pet_type",
      title: "Pet. Entity Type",
      width: 200,
      render: val => (val ? PET_TYPE_MAP[upperFirst(val.toLowerCase())] : val)
    },
    {
      dataIndex: "patent_owners",
      title: "Owner",
      width: 200,
      render: companiesRenderer
    },
    {
      dataIndex: "ptab_appl_number",
      title: "Application #",
      width: 120
    },
    {
      dataIndex: "ptab_tech_center",
      title: "Tech Center",
      width: 90
    },
    {
      dataIndex: "up_industry",
      title: "Industry",
      width: 80,
      render: v => v ? {
        T: "High-Tech",
        M: "Medical",
        O: "Other"
      }[v] : v
    },
    {
      dataIndex: "up_po_type",
      title: "P.O. Entity Type",
      width: 170
    },
    {
      dataIndex: ["lawfirms", "petitioner"],
      title: "Pet. Counsel",
      width: 200,
      render: toSetString
    },
    {
      dataIndex: ["lawfirms", "patent_owner"],
      title: "P.O. Counsel",
      width: 200,
      render: toSetString
    },
    {
      dataIndex: ["judges", "author"],
      title: "Author Judge",
      width: 200,
      render: toSetString
    },
    {
      dataIndex: ["judges", "panel"],
      title: "Panel Judge",
      width: 200,
      render: toSetString
    }
  ];
  if (shouldExport) {
    allCols = allCols.concat([
      {
        dataIndex: "claim_outcome_at_institution",
        title: "Claim Outcome at Institution",
        width: 90,
        render: toString
      },
      {
        dataIndex: "claim_outcome_at_fwd",
        title: "Claim Outcome at FWD",
        width: 90,
        render: toString
      },
      {
        dataIndex: "is_joined_case",
        title: "Joined",
        width: 90,
        render: toString
      },
      {
        dataIndex: "is_on_appeal",
        title: "On Appeal",
        width: 90,
        render: toString
      },
      {
        dataIndex: "is_on_remand",
        title: "On Remand",
        width: 90,
        render: toString
      }
    ]);
  }
  const expandable = queries["filings.full_text"] ? {
    defaultExpandAllRows: true,
    rowExpandable: record => record && record.dockets && record.dockets.length > 0,
    expandedRowRender: record => (
      <List
        dataSource={record.dockets}
        renderItem={d => (
          <List.Item>
            {toFilingS3Link(`#${d.docket_number}`, d)}
            {", "}
            {toDateString(d.filing_date)}
            {", "}
            {d.docket_text}
            {d.highlight ? (
              <List
                dataSource={d.highlight}
                renderItem={text => (
                  <List.Item>
                    <span dangerouslySetInnerHTML={{ __html: text }} />
                  </List.Item>
                )}
                style={styles.subList}
              />
            ) : null}
          </List.Item>
        )}
        size="small"
      />
    ),
  } : undefined;
  const fetcher = q => getCases(q).then(json => ({
    data: json.hits.hits.map(e =>
      e.inner_hits
        ? {
          // include dockets for full-text search if there's any
          ...e._source,
          dockets: e.inner_hits.filings.hits.hits.map(f => ({
            ...f._source,
            pdf_id: `ptab-filings/${e._source.case_number}/${f._source.docket_number}`, // for generating S3 link
            highlight: f.highlight ? f.highlight["filings.full_text"] : undefined
          }))
        }
        : e._source
    ),
    total: json.hits.total.value,
  }));
  return (
    <TableWithControls
      queries={queriesCurrent}
      fetcher={fetcher}
      onChange={q => {
        setQueriesCurrent(q);
        onQueryChange(q);
      }}
      columns={allCols}
      columnKeysSelected={colIndicesDefault}
      rowKey="case_number"
      tableKey="ptabTable"
      expandable={expandable}
      size={size}
      shouldSelectColumns={shouldSelectColumns}
      shouldExport={shouldExport}
      exportESUrl={ES_URL}
      exportESQueryBuilder={esqCases}
    />
  )
}

CasesTableContainer.propTypes = {
  queries: PropTypes.object.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  size: PropTypes.oneOf(["small", "middle", "large"]),
  shouldSelectColumns: PropTypes.bool,
  shouldExport: PropTypes.bool
};

export default withTopCompanies(CasesTableContainer);
